import React, { useContext, useEffect, useMemo } from "react";
import { HashRouter, Route, RouteComponentProps } from "react-router-dom";
import uuid from "uuid/v4";

import { getQueryString } from "../../../../core/helpers";
import { useTrackPageView } from "../../../../lib/analytics";
import { AppContext } from "../../../../lib/appContext";
import { useAttributionContext } from "../../../../lib/attributionContext";
import { router } from "../../../../routes";
import { SocketContext } from "../../../common/context/quoteFormConnector";
import { useFormEventTracker } from "../../../common/eventTracker";
import { useQuoteFormState } from "../../state";
import { RouterParams } from "../../types";
import { CountryCode, getDynamicWizardQuoteForm } from "./getDynamicWizardQuoteForm";
import { getFormType } from "./helpers";
import { RedirectToFirstStep } from "./RedirectToFirstStep";
import { usePrefill } from "./usePrefill";
import { useFormValuesQueryParams } from "./useFormValuesQueryParams";
import { FormSubmitMutation } from "../../../../queries/quote/components/FormSubmitMutation";
import {
  createSetStepFromUrl,
  createStepChangeHandler,
  handleSubmit
} from "../../helpers/quoteForms";
import { withLoginForm } from "../../../../components/GenericLogin";
import { useCaptchaConfig } from "../../hooks";
import { getConfig } from "../../../../../config/app";

const QuoteFormBase: React.FC<RouteComponentProps<RouterParams>> = ({ history, match }) => {
  const { Router } = router;

  if (!Router.router) {
    return null;
  }

  const { business, formId, noPrefill } = Router.router.query;
  const { stepId } = match.params;

  useTrackPageView(stepId);
  const [isLoading] = useQuoteFormState("isLoading");
  const [form] = useQuoteFormState("form");
  const { formStepRenderEvent, sendFormSubmitEvent } = useFormEventTracker();
  const formValuesQueryParams = useFormValuesQueryParams();
  const attribution = useAttributionContext()();
  const { saveQuoteForm, getQuoteFormById } = useContext(SocketContext);
  const {
    countryCode,
    businessUnitConfig: { businessCode }
  } = useContext(AppContext);
  const {
    persisted: isPersisted,
    getValues: getPersistedValues,
    formSubscribe: onValidForm
  } = usePrefill({ noPrefill: noPrefill !== undefined });
  const captchaConfig = useCaptchaConfig();

  const setStepFromURL = createSetStepFromUrl(history);
  const hasFormId = Boolean(form && form.id);
  const isExtended: boolean = useMemo(() => isPersisted || hasFormId, [form]);
  const formType = getFormType(isExtended, isPersisted);

  if (hasFormId && form!.id === formId) {
    const queryString = getQueryString(window.location.href);
    Router.replaceRoute("quoteForm", { ...queryString, business, formId: uuid() });
  }

  useEffect(() => {
    if (formId && !hasFormId) {
      getQuoteFormById(formId);
    }
  }, [formId]);

  const formProps = {
    router: {
      push: (id: string) => history.push(`/${id}`),
      replace: (id: string) => history.replace(`/${id}`)
    },
    stepId
  };

  const DynamicWizardQuoteForm = getDynamicWizardQuoteForm(countryCode);

  return form ? (
    <FormSubmitMutation>
      {submitMutationFn => (
        <DynamicWizardQuoteForm
          {...formProps}
          handleSubmit={handleSubmit({
            submitMutationFn,
            formId,
            formType,
            sendFormSubmitEvent,
            attribution,
            saveQuoteForm,
            businessCode
          })}
          isSubmitting={isLoading}
          values={hasFormId ? { ...form } : { ...getPersistedValues(), ...formValuesQueryParams }}
          onStepChange={createStepChangeHandler({
            isExtended,
            isPersisted,
            formStepRenderEvent,
            history,
            formId
          })}
          onValidForm={onValidForm}
          extended={isExtended}
          formProps={{
            decorators: [setStepFromURL]
          }}
          {...captchaConfig}
          {...(countryCode === CountryCode.Brazil && {
            bffURL: getConfig("BFF.br")
          })}
        />
      )}
    </FormSubmitMutation>
  ) : null;
};

const QuoteForm: React.FC = () => (
  <HashRouter hashType="noslash">
    <Route path="/" exact={true} component={RedirectToFirstStep} />
    <Route path="/:stepId" exact={true} component={withLoginForm(QuoteFormBase)} />
  </HashRouter>
);

export { QuoteForm };
